// HeadcountTable.jsx — the "Headcount (Yearly View)" block of the Recruitment Dashboard sheet:
// one row per metric, one column per month of the fiscal year. Metrics with a
// per-service-line breakdown (Headcount, Budget, Delta) expand and collapse.

import { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { formatMonth, todayIso } from '@/utils/recruitment/recruitment';

import { recruitmentCss as css } from '@digital_monorepo/styling';

const formatValue = value => {
  if (value === null || value === undefined) return '—';
  const rounded = Math.round(value * 10) / 10;
  return rounded < 0 ? `−${Math.abs(rounded)}` : String(rounded);
};

const valueClass = value => {
  if (value === null || value === undefined || value === 0) return css.valueMuted;
  return value < 0 ? css.valueNegative : '';
};

// Groups each top-level metric with the breakdown rows that follow it.
const groupRows = headcount => {
  const bySortOrder = new Map();
  headcount.forEach(h => {
    if (!bySortOrder.has(h.SortOrder)) bySortOrder.set(h.SortOrder, { sortOrder: h.SortOrder, metric: h.Metric, level: h.Level, values: {} });
    bySortOrder.get(h.SortOrder).values[h.Month] = h.Value;
  });
  const groups = [];
  [...bySortOrder.values()]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .forEach(row => {
      if (row.level && groups.length) groups[groups.length - 1].children.push(row);
      else groups.push({ ...row, children: [] });
    });
  return groups;
};

const HeadcountTable = ({ headcount }) => {
  const months = useMemo(() => [...new Set(headcount.map(h => h.Month))].sort(), [headcount]);
  const groups = useMemo(() => groupRows(headcount), [headcount]);
  const [expanded, setExpanded] = useState(() => new Set());

  const currentMonth = `${todayIso().slice(0, 7)}-01`;
  const expandable = groups.filter(g => g.children.length);
  const allExpanded = expandable.length > 0 && expandable.every(g => expanded.has(g.sortOrder));

  const toggle = sortOrder => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(sortOrder)) next.delete(sortOrder);
    else next.add(sortOrder);
    return next;
  });

  if (!groups.length) {
    return <p className={css.emptyNote}>No headcount data for this fiscal year.</p>;
  }

  const monthCells = (row, isChild) => months.map(m => (
    <td key={m} className={`${valueClass(row.values[m])} ${m === currentMonth ? css.currentMonth : ''} ${isChild ? '' : css.groupValue}`}>
      {formatValue(row.values[m])}
    </td>
  ));

  return (
    <>
      <div className={css.tableToolbar}>
        <button
          type='button'
          className={css.linkButton}
          onClick={() => setExpanded(allExpanded ? new Set() : new Set(expandable.map(g => g.sortOrder)))}>
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      <div className={css.tableScroll}>
        <table className={css.headcountTable}>
          <thead>
            <tr>
              <th scope='col' className={css.metricHeader}>Metric</th>
              {months.map(m => (
                <th key={m} scope='col' className={m === currentMonth ? css.currentMonth : ''} aria-current={m === currentMonth ? 'date' : undefined}>
                  {formatMonth(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map(group => {
              const open = expanded.has(group.sortOrder);
              return (
                <Fragment key={group.sortOrder}>
                  <tr className={`${css.groupRow} ${open ? css.groupRowOpen : ''}`}>
                    <th scope='row' className={css.metricCell}>
                      {group.children.length ? (
                        <button type='button' className={css.expandButton} aria-expanded={open} onClick={() => toggle(group.sortOrder)}>
                          <span className={css.chevron} aria-hidden='true' />
                          {group.metric}
                          <span className={css.expandCount}>{group.children.length}</span>
                        </button>
                      ) : (
                        <span className={css.metricLabel}>{group.metric}</span>
                      )}
                    </th>
                    {monthCells(group, false)}
                  </tr>
                  {open && group.children.map(child => (
                    <tr key={child.sortOrder} className={css.childRow}>
                      <th scope='row' className={css.metricCell}>{child.metric}</th>
                      {monthCells(child, true)}
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

HeadcountTable.propTypes = {
  headcount: PropTypes.arrayOf(PropTypes.shape({
    SortOrder: PropTypes.number,
    Metric: PropTypes.string,
    Level: PropTypes.number,
    Month: PropTypes.string,
    Value: PropTypes.number
  })).isRequired
};

export default HeadcountTable;
